import {
  FC,
  MutableRefObject,
  useEffect, useMemo, useRef, useState,
} from 'react';

// Third-party
import Stack from '@mui/material/Stack';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import moment, { Moment } from 'moment-timezone';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';

// Hooks
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import {
  getCalendarDates, TableColumnHeader, TimeoffBlock, hasTimeOff, isSameOrAfterSchedule,
} from '@mgm/common/features/schedules';
import { getTimezone } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { useScheduleFilters, useSchedules } from './hooks';

// Components
import { ScheduleAvatar, ShiftPopover } from '../Shared/components';
import {
  CreateShift,
  ScheduleFooter,
  ScheduleShifts,
  TableMainHeader,
} from './components';

// Redux-toolkit
import { getScheduleList, getSelectedLocationId, getTimeOffs } from '../../../store/schedules/selectors';

// Utils
import { dateFormat } from '../../../utils/helpers';
import { useSchedulePermissions } from '../Shared/hooks';
import 'moment/dist/locale/es';
import { useGetScheduleDataAPI } from '../../../hooks/api/useSchedules';
import { getIsPrintMode } from '../../../store/common/selectors';
import { useCommon } from '../../../hooks/useCommon';
import { schedulesActions } from '../../../store/schedules/slice';
import { MemoizedFilterTabs } from '../../../components/base';
import { INDEX_TO_COLOR } from '../../../data';

type TSchedule = {
  setSelectedDate: (date: Moment) => void;
  selectedDate: Moment
};

const Schedule: FC<TSchedule> = ({ setSelectedDate, selectedDate }) => {
  const { t } = useTranslation(['schedule', 'common']);
  const theme = useTheme();
  const selectedLocationId = useAppSelector(getSelectedLocationId);

  const componentRef = useRef<HTMLDivElement | null>(null);
  const [dates, setDates] = useState<any>(null);
  const { refetch: refetchShifts, isRefetching, isFetching } = useGetScheduleDataAPI({ dates, isMainSchedulesPage: true });

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(140);
  const { getPersonShifts } = useSchedules();
  const { getMembersByFilters } = useScheduleFilters();
  const { schedulePermissions } = useSchedulePermissions();
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const scheduleList = useAppSelector(getScheduleList);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const dispatch = useAppDispatch();
  const { locationsByPermissions } = useCommon();
  const timeoffs = useAppSelector(getTimeOffs);

  // Filter schedules within the selected week
  const formattedScheduleList = useMemo(() => {
    const startOfWeek = moment(selectedDate).startOf('week');
    const endOfWeek = moment(selectedDate).endOf('week');

    return Object.values(scheduleList).flatMap((day) => day.schedules.filter((schedule) => {
      const scheduleDate = moment(schedule.start);
      return scheduleDate.isBetween(startOfWeek, endOfWeek, null, '[]'); // Inclusive of the start and end date
    }));
  }, [scheduleList, selectedDate, selectedDate]);

  const getLengthByStatus = (locationId: number) => formattedScheduleList.reduce(
    (count, item) => (item.location.id === locationId ? count + 1 : count),
    0,
  );

  const tabs = locationsByPermissions.map((location, index) => ({
    value: location.id,
    label: location.name,
    color: INDEX_TO_COLOR[index],
    count: getLengthByStatus(location.id),
  }));

  tabs.unshift({
    value: 0,
    label: t('all', { ns: 'common' }),
    color: 'info',
    count: formattedScheduleList.length,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Track current month range
  const [currentMonthRange, setCurrentMonthRange] = useState([moment().startOf('month'), moment().endOf('month')]);

  useEffect(() => {
    if (dates) refetchShifts();
  }, [dates]);

  const isXL = useMediaQuery(theme.breakpoints.up('xl'));

  const getMembersWithFooter = useMemo(() => {
    if (isPrintMode) {
      return getMembersByFilters;
    }

    if (schedulePermissions.isAllowViewSalaryData) {
      return [...getMembersByFilters, {
        scheduleHours: t('scheduledHours'),
        laborCost: t('laborCost'),
        // employees: 'EMPLOYEES',
        type: 'footer',
        isFooter: true,
      }];
    }
    return getMembersByFilters;
  }, [getMembersByFilters, isPrintMode]);

  const scheduleColumns = useMemo(() => {
    const baseHeader: Array<GridColDef> = [
      {
        field: 'name',
        headerName: dateFormat(selectedDate, 'MMMM, YYYY'),
        renderHeader: (params) => (
          <Typography variant="body2" pl={1}>{params.colDef.headerName}</Typography>
        ),
        sortable: false,
        width: 200,
        flex: 0,
        renderCell: ({ row }) => (
          <Stack>
            {!row.isFooter
              ? (
                <ScheduleAvatar selectedDate={selectedDate} member={row} />
              )
              : (
                <Stack direction="column" paddingX={1.5}>
                  <Typography
                    pt={1}
                    textAlign="left"
                    variant="caption"
                    color="primary"
                  >
                    {row.scheduleHours}
                  </Typography>
                  <Typography
                    textAlign="left"
                    variant="caption"
                    color="primary"
                  >
                    {row.laborCost}
                  </Typography>
                  <Typography
                    textAlign="left"
                    pb={1}
                    variant="caption"
                    color="primary"
                  >
                    {row.employees}
                  </Typography>
                </Stack>
              )}
          </Stack>
        ),
        valueGetter: (_, row) => startCase(row.name),
      },
    ];

    const headers: Array<GridColDef> = getCalendarDates(selectedDate).map((date) => ({
      field: date.startOf('day').format(),
      headerName: date.format('ddd'),
      width: isXL ? 220 : 170,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      flex: 0,
      renderCell: (params) => {
        const memberShifts = getPersonShifts(params.row.email, moment.tz(params.field, getTimezone()));

        const dateFormated = moment.tz(params.field, getTimezone()).format();

        const isTimeOff = hasTimeOff(timeoffs, params.row.email, params.field as string);

        if (params.row.isFooter) return schedulePermissions.isAllowViewSalaryData && <ScheduleFooter selectedDate={params.field} />;

        if (isTimeOff) {
          return (
            <TimeoffBlock locale={{
              timeoff: t('timeoff'),
            }}
            />
          );
        }

        if (memberShifts?.length) {
          return <ScheduleShifts setAnchorEl={setAnchorEl} memberShifts={memberShifts} />;
        }

        return (
          <Stack
            height="100%"
            width="100%"
          >
            {isSameOrAfterSchedule(dateFormated) && schedulePermissions.isAllowManage && <CreateShift member={params.row} date={params.field} />}
          </Stack>
        );
      },
      renderHeader: (params) => (
        <TableColumnHeader date={params.field} />
      ),
    }));

    return [...baseHeader, ...headers] as Array<GridColDef>;
  }, [selectedDate, scheduleList, selectedLocationId, timeoffs]);

  const handleDayChange = (date?: Moment) => {
    const formattedDate = date || selectedDate;

    // Start and end of the week for the given date (Monday to Sunday)
    const startOfWeek = moment(formattedDate).startOf('isoWeek'); // Monday
    const endOfWeek = moment(formattedDate).endOf('isoWeek'); // Sunday

    // Calculate start and end of the current month
    const startOfMonth = moment(formattedDate).startOf('month');
    const endOfMonth = moment(formattedDate).endOf('month');

    // Calculate the date range including cross-month days
    const fromDate = moment.min(startOfWeek, startOfMonth).startOf('day');
    const toDate = moment.max(endOfWeek, endOfMonth).endOf('day');

    // Check if the new date range is outside the current month range
    if (fromDate.isBefore(currentMonthRange[0]) || toDate.isAfter(currentMonthRange[1])) {
      setDates([fromDate, toDate]);
      setCurrentMonthRange([fromDate, toDate]);
    }
  };

  const handleFilterStatus = (newValue: number) => {
    dispatch(schedulesActions.setSelectedLocationId(newValue));
  };

  const onToday = () => {
    setSelectedDate(moment());
    handleDayChange(moment());
  };

  const onPrevDate = () => {
    const newDate = moment(selectedDate).subtract(1, 'week');
    setSelectedDate(newDate);
    handleDayChange(newDate);
  };

  const onNextDate = () => {
    const newDate = moment(selectedDate).add(1, 'week');
    setSelectedDate(newDate);
    handleDayChange(newDate);
  };

  return (
    <Stack ref={componentRef}>
      <div ref={resizableDiv}>
        <Card sx={{
          padding: 0,
        }}
        >
          {!isPrintMode && <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />}
          <TableMainHeader
            date={selectedDate}
            onNextDate={onNextDate}
            onPrevDate={onPrevDate}
            onToday={onToday}
            printRef={componentRef}
          />
          <Card
            sx={{
              // eslint-disable-next-line no-nested-ternary
              height: isDesktopSmallScreen ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto',
              overflow: 'auto',
              boxShadow: 'unset',
              padding: 0,
            }}
          >
            <Divider sx={{ borderStyle: 'dashed' }} />
            <DataGridPro
              apiRef={apiRef}
              getRowHeight={() => 'auto'}
              density="standard"
              rows={getMembersWithFooter}
              disableRowSelectionOnClick
              loading={isFetching || isRefetching}
              showCellVerticalBorder
              disableColumnMenu
              showColumnVerticalBorder
              initialState={{ pinnedColumns: { left: ['name'] } }}
              slots={{
                loadingOverlay: LinearProgress as any,
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  padding: 0,
                  alignItems: 'baseline',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'inherit',
                },
                '.MuiDataGrid-main > *:first-of-type': {
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                },
              }}
              {...(schedulePermissions.isAllowViewSalaryData && {
                pinnedRows: {
                  bottom: [getMembersWithFooter[getMembersWithFooter.length - 1]],
                },
              })}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  printOptions: {
                    hideFooter: true,
                    hideToolbar: true,
                  },
                },
              }}
              getRowId={(row) => row.email || row.type}
              columns={scheduleColumns}
              hideFooterPagination
              hideFooter
              checkboxSelection={false}
              className={isPrintMode ? 'print-mode' : ''}
            />
          </Card>
        </Card>
      </div>
      <ShiftPopover anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
    </Stack>
  );
};

export {
  Schedule,
};
